import { Injectable, Injector, inject } from '@angular/core';
import { ActionsSubject, Store } from '@ngrx/store';
import { map, take } from 'rxjs/operators';
import { State, User } from '@wam/shared';
import { getFromLocalStorage } from './authentication/user.data';
import { Observable, of } from 'rxjs';
import * as authenticationActions from './authentication/state/authentication.actions';
import { ofType } from '@ngrx/effects';
import { isNil } from 'lodash-es';
import { LayoutService } from '@app/layouts/layout.service';
import { isReportsApp } from '@app/reports-app/reports-app.util';
import { ReportsAppAccessService } from '@app/reports-app/reports-app-access.service';
import { AuthenticationService } from './authentication/authentication.service';

@Injectable({
  providedIn: 'root',
})
export class InitializerService {
  private readonly store = inject<Store<State>>(Store);
  private readonly layoutService = inject(LayoutService);
  private readonly actionListener = inject(ActionsSubject);
  private readonly injector = inject(Injector);

  constructor() {
    this.layoutService.initialize();
  }

  load(): Observable<boolean> {
    if (isReportsApp()) {
      // The standalone reports app authorizes from the `idToken` query param only -
      // there is no login redirect and no token refresh. Resolved lazily so the teacher and
      // admin apps never construct the reports-app dependency graph.
      return this.injector.get(ReportsAppAccessService).authorize();
    }

    return this.restoreUser();
  }

  private restoreUser(): Observable<boolean> {
    const isLoggedInPage = window.location.pathname.includes('logged-in');
    const user = this.withPrimaryRole(getFromLocalStorage());

    if (this.userIsAlreadyLoggedIn(isLoggedInPage, user)) {
      this.store.dispatch(authenticationActions.updateCurrentUser({ user, refreshed: true }));

      return this.actionListener.pipe(
        ofType(authenticationActions.logInSuccess, authenticationActions.logOut),
        take(1),
        map(() => true),
      );
    } else if (this.shouldLogIn(isLoggedInPage, user)) {
      this.store.dispatch(authenticationActions.logIn());

      return this.actionListener.pipe(
        ofType(authenticationActions.logInActionSuccess),
        take(1),
        map(() => true),
      );
    }

    return of(false);
  }

  // Users persisted before `primaryRole` was decoded need it backfilled from their stored token.
  private withPrimaryRole(user: User | null): User | null {
    if (!user?.idToken || user.primaryRole) {
      return user;
    }

    try {
      const { primaryRole } = this.injector
        .get(AuthenticationService)
        .buildUser(user.idToken, user.refreshToken, user.type);
      return { ...user, primaryRole };
    } catch {
      return user;
    }
  }

  private userIsAlreadyLoggedIn(isLoggedInPage: boolean, user: User): boolean {
    return !isLoggedInPage && !isNil(user);
  }

  private shouldLogIn(isLoggedInPage: boolean, user: User): boolean {
    return !(isLoggedInPage || user);
  }
}
